/**
 * Scheduling against the general report engine (/reports/scheduled) — any
 * template in REPORT_TEMPLATES, not just the saved-pipeline-view-scoped
 * schedules pipelineViewsService talks to. Backs the Reporting page's
 * "Scheduled Reports" section for Portfolio/Surveillance/Term Sheet/Deal
 * Package/Deal Excel Workbook.
 */
import api from './api'
import type {
  ReportDeliveryHistory,
  ReportEmailDefaults,
  ReportEmailPreview,
  ReportSchedule,
  ReportScheduleInput,
} from '@/pages/pipeline-intelligence/types'

/**
 * Reads the raw snake_case fields from backend/app/api/v1/reports.py's
 * _serialize_schedule directly — deliberately NOT routed through the shared
 * toCamelCase() transform. Its KEY_MAP has trigger_type -> 'type' (built for
 * Loan.triggers[]), which would silently rename this endpoint's trigger_type
 * to `type` and drop event-triggered schedules to a wrong 'time' default.
 */
function mapSchedule(data: unknown): ReportSchedule {
  const row = (data ?? {}) as Record<string, unknown>
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    template: String(row.template ?? ''),
    frequency: String(row.frequency ?? 'weekly'),
    dayOfWeek: (row.day_of_week as number | null | undefined) ?? null,
    dayOfMonth: (row.day_of_month as number | null | undefined) ?? null,
    hourUtc: Number(row.hour_utc ?? 12),
    minuteLocal: Number(row.minute_local ?? 0),
    timezone: String(row.timezone ?? 'UTC'),
    businessDaysOnly: Boolean(row.business_days_only),
    triggerType: String(row.trigger_type ?? 'time'),
    triggerEventType: (row.trigger_event_type as string | null | undefined) ?? null,
    recipients: Array.isArray(row.recipients) ? (row.recipients as string[]) : [],
    filters: (row.filters as Record<string, unknown>) ?? {},
    format: String(row.format ?? 'pdf'),
    deliveryMode: String(row.delivery_mode ?? 'none'),
    senderEmail: (row.sender_email as string | null | undefined) ?? null,
    emailSubject: (row.email_subject as string | null | undefined) ?? null,
    emailPreheader: (row.email_preheader as string | null | undefined) ?? null,
    emailBody: (row.email_body as string | null | undefined) ?? null,
    isActive: Boolean(row.is_active),
    pipelineViewId: (row.pipeline_view_id as string | null | undefined) ?? null,
    // Absent from a server that predates them: undefined, never a guess that
    // an email would or would not go out.
    willEmail: typeof row.will_email === 'boolean' ? row.will_email : undefined,
    deliveryBlockers: Array.isArray(row.delivery_blockers)
      ? (row.delivery_blockers as ReportSchedule['deliveryBlockers'])
      : undefined,
    recipientApproval:
      row.recipient_approval && typeof row.recipient_approval === 'object'
        ? (row.recipient_approval as Record<string, boolean>)
        : undefined,
    createdBy: (row.created_by as string | null | undefined) ?? null,
    lastRunAt: (row.last_run_at as string | null | undefined) ?? null,
    lastRunStatus: (row.last_run_status as string | null | undefined) ?? null,
    nextRunAt: (row.next_run_at as string | null | undefined) ?? (row.next_run as string | null | undefined) ?? null,
    createdAt: (row.created_at as string | null | undefined) ?? null,
    updatedAt: (row.updated_at as string | null | undefined) ?? null,
  }
}

/** What the scheduler on this deployment will actually do (GET /reports/scheduled/status). */
export type ReportSchedulerStatus = {
  generationEnabled: boolean
  deliveryEnabled: boolean
  reviewerDeliveryEnabled: boolean
  teamDeliveryEnabled: boolean
  /** The master send switch. Absent from a server that predates it. */
  autosendEnabled?: boolean
  /** Whether any recipient is approved for scheduled email (never the list itself). */
  allowlistConfigured?: boolean
  slaDigestEnabled: boolean
  summary: string
  productEvents: Array<{ type: string; label: string }>
}

/**
 * A saved schedule as the update endpoint wants it back.
 *
 * The endpoint REPLACES the schedule with what it is sent, and every field it
 * is not sent falls to its default. `minuteLocal` was left out of the two
 * callers that re-submit a schedule whole (pause, subscribe), so a schedule
 * set for 7:30 AM came back at 7:00 AM the first time anyone paused it. This
 * is the one place that spells the whole body, so no caller can drop a field.
 */
export function scheduleInput(
  schedule: ReportSchedule,
  overrides: Partial<ReportScheduleInput> = {},
): ReportScheduleInput {
  return {
    name: schedule.name,
    template: schedule.template,
    frequency: schedule.frequency,
    dayOfWeek: schedule.dayOfWeek,
    dayOfMonth: schedule.dayOfMonth,
    hourUtc: schedule.hourUtc,
    minuteLocal: schedule.minuteLocal,
    timezone: schedule.timezone,
    businessDaysOnly: schedule.businessDaysOnly,
    triggerType: schedule.triggerType as ReportScheduleInput['triggerType'],
    triggerEventType: schedule.triggerEventType,
    recipients: schedule.recipients,
    filters: schedule.filters,
    format: schedule.format as ReportScheduleInput['format'],
    deliveryMode: schedule.deliveryMode as ReportScheduleInput['deliveryMode'],
    senderEmail: schedule.senderEmail,
    emailSubject: schedule.emailSubject,
    emailPreheader: schedule.emailPreheader,
    emailBody: schedule.emailBody,
    ...overrides,
  }
}

/**
 * How a recipient list is delivered: nobody is retain-only, one address is a
 * reviewer copy, several is a team distribution. The server refuses to activate
 * a retain-only schedule that names recipients, and a reviewer schedule that
 * names two, so any change to the list has to restate the mode with it.
 */
export function deliveryModeFor(recipients: string[]): 'none' | 'reviewer' | 'team' {
  return recipients.length === 0 ? 'none' : recipients.length === 1 ? 'reviewer' : 'team'
}

export const reportSchedulesService = {
  list: () => api.get('/reports/scheduled').then((r) => (r.data as unknown[]).map(mapSchedule)),

  getStatus: () =>
    api.get('/reports/scheduled/status').then((r) => r.data as ReportSchedulerStatus),

  create: (body: ReportScheduleInput) =>
    api.post('/reports/scheduled', body).then((r) => mapSchedule(r.data)),

  update: (id: string, body: ReportScheduleInput) =>
    api.put(`/reports/scheduled/${id}`, body).then((r) => mapSchedule(r.data)),

  getEmailDefaults: () =>
    api.get('/reports/scheduled/email-defaults').then((r) => r.data as ReportEmailDefaults),

  previewEmail: (body: ReportScheduleInput) =>
    api.post('/reports/scheduled/email-preview', body).then((r) => r.data as ReportEmailPreview),

  getDeliveryHistory: (limit = 150) =>
    api
      .get('/reports/scheduled/delivery-history', { params: { limit } })
      .then((r) => r.data as ReportDeliveryHistory),

  activate: (id: string) =>
    api.post(`/reports/scheduled/${id}/activate`).then((r) => mapSchedule(r.data)),

  /** No dedicated pause endpoint server-side — any update forces isActive
   *  false, so re-submitting the schedule unchanged is the pause action. */
  pause: (id: string, current: ReportSchedule) =>
    api.put(`/reports/scheduled/${id}`, scheduleInput(current)).then((r) => mapSchedule(r.data)),

  remove: (id: string) => api.delete(`/reports/scheduled/${id}`).then(() => undefined),

  runNow: (id: string) =>
    api.post(`/reports/scheduled/${id}/run-now`).then((r) => r.data as Record<string, unknown>),
}
